import {
  Backdrop,
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  MenuItem,
  Menu as MuiMenu,
  SvgIcon,
  SvgIconTypeMap,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { Theme, useTheme } from '@mui/material/styles';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FC, MouseEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import IconButton, { IconButtonProps } from '../IconButton';
import { Row } from '../Layout';

const MenuList = styled(MuiMenu)({
  margin: 0,
  padding: 0,
  zIndex: 2010,
});

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  fontSize: theme.fontSizes.regular,
  lineHeight: '20px',
  fontWeight: theme.typography.fontWeightMedium,
}));

const ItemIcon = styled(SvgIcon)(({ theme }) => ({
  fontSize: theme.fontSizes.xl,
}));

const RequiresActionBadge = styled(Badge)(({ theme }) => ({
  marginLeft: theme.spacing(theme.spacingSizes.xxs),
}));

const TransparentBackdrop = styled(Backdrop)({
  backgroundColor: 'transparent',
});

const TopEdgeIconButton = styled(IconButton)(({ theme }) => ({
  marginTop: theme.spacing(-theme.spacingSizes.sm),
}));

const EndIcon = styled(SvgIcon)(({ theme }) => ({
  marginLeft: theme.spacing(theme.spacingSizes.xxs),
}));

type Props = {
  handleIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  handleVariant?: IconButtonProps['variant'];
  buttonVariant?: ButtonProps['variant'];
  handleSize?: 'small' | 'medium';
  handleEdge?: IconButtonProps['edge'] | 'top';
  className?: string;
  items: Array<{
    label: string;
    icon: OverridableComponent<SvgIconTypeMap<object>>;
    onClick: () => void;
    href?: string;
    disabled?: boolean;
    tooltip?: string;
    requiresAction?: boolean;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  }>;
  alignWithIconRightEdge?: boolean;
  label?: string;
  loading?: boolean;
  transparentBackdrop?: boolean;
  gap?: keyof Theme['spacingSizes'];
};

const Menu: FC<Props> = ({
  handleIcon: Handle,
  items,
  className,
  handleSize = 'medium',
  handleEdge,
  handleVariant = 'default',
  buttonVariant = 'outlined',
  alignWithIconRightEdge = false,
  label,
  loading = false,
  transparentBackdrop = true,
  gap,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!items.length) return null;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  }) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleButton = label ? (
    <Button
      onClick={handleClick}
      variant={buttonVariant}
      size="large"
      endIcon={
        loading ? (
          <CircularProgress color="inherit" size={20} />
        ) : Handle ? (
          <EndIcon as={Handle} fontSize="small" />
        ) : undefined
      }
      disabled={loading}
    >
      {label}
    </Button>
  ) : Handle ? (
    handleEdge === 'top' ? (
      <TopEdgeIconButton
        variant={handleVariant}
        onClick={handleClick}
        size={handleSize}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          <Handle fontSize="small" />
        )}
      </TopEdgeIconButton>
    ) : (
      <IconButton
        variant={handleVariant}
        onClick={handleClick}
        size={handleSize}
        edge={handleEdge}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          <Handle fontSize="small" />
        )}
      </IconButton>
    )
  ) : null;

  return (
    <Box className={className}>
      {handleButton}
      <MenuList
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={
          gap
            ? {
                paper: {
                  sx: {
                    marginTop: theme.spacing(theme.spacingSizes[gap]),
                  },
                },
              }
            : undefined
        }
        slots={{
          backdrop: transparentBackdrop ? TransparentBackdrop : undefined,
        }}
        {...(alignWithIconRightEdge
          ? {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            }
          : {})}
      >
        {items.map(
          ({
            label,
            icon,
            onClick,
            href,
            disabled,
            tooltip,
            requiresAction,
            color,
          }) => {
            const itemChildren = (
              <Row centered spacing="xs">
                <ItemIcon as={icon} fontSize="small" color={color} />
                <Label variant="body2">{label}</Label>
                {requiresAction ? (
                  <RequiresActionBadge variant="dot" color="warning" />
                ) : null}
              </Row>
            );
            return (
              <MenuItem
                key={label}
                disabled={disabled}
                {...(href ? { component: 'a', href } : {})}
                onClick={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  if (href && (e.metaKey || e.ctrlKey)) return;
                  e.preventDefault();
                  flushSync(() => {
                    handleClose();
                  });
                  onClick();
                }}
                style={{
                  pointerEvents: disabled && tooltip ? 'auto' : undefined,
                }}
              >
                {tooltip ? (
                  <Tooltip title={tooltip}>
                    <Box sx={{ width: '100%' }}>{itemChildren}</Box>
                  </Tooltip>
                ) : (
                  itemChildren
                )}
              </MenuItem>
            );
          },
        )}
      </MenuList>
    </Box>
  );
};

export default Menu;
